import { z } from "zod";
import { OrigemDaVisita, Visitante } from "./analise.js";
/**
 * O que a pagina de checkout (area do trader) troca com o servico de pagamentos (spec 003, secao 5). A pagina usa os
 * TIPOS (sem levar o zod pro navegador) e o servico valida com os SCHEMAS; a fumaca de producao monta o corpo com os
 * mesmos schemas (decisao 12): mudanca que quebre a pagina quebra a compilacao dela e a fumaca juntas.
 */
/** Abre a tentativa (4.1, passo 2). CPF e telefone chegam como digitados; o servico normaliza. */
export const NovaTentativa = z.object({
    /**
     * Nome, CPF e e-mail: com o cliente logado (spec 045) vem da conta e o servico ignora o que vier aqui. So o checkout
     * interno de verificacao (a fumaca) ainda manda os tres.
     */
    nome: z.string().trim().min(3, "informe o nome completo").max(120).optional(),
    cpf: z.string().trim().min(11, "CPF incompleto").max(14).optional(),
    email: z.string().trim().email("e-mail invalido").max(200).optional(),
    telefone: z.string().trim().min(10, "telefone com DDD").max(20),
    /** So quando o checkout tem termos: a versao que a pagina mostrou e o comprador aceitou. */
    aceite_termos_versao: z.string().trim().min(1).max(50).optional(),
    /** O regulamento que a pagina mostrou (spec 042): a versao (grupo) do nucleo, com uma caixa marcada por documento. */
    aceite_regulamento: z.object({ grupo: z.number().int().positive(), documentos: z.array(z.string().min(1).max(60)).min(1).max(10) }).optional(),
    /** So quando o checkout deixa escolher (`opcoes_de_plataforma`): Profit One no preco base, Profit Pro com o adicional. */
    plataforma: z.enum(["PROFIT_ONE", "PROFIT_PRO"]).optional(),
    /** Beneficios da oferta que o comprador marcou (spec 013, item 2), pelos ids da vitrine. */
    beneficios: z.array(z.string().min(1)).max(8).default([]),
    /** Cupom de desconto (spec 016): o uso fica reservado nesta tentativa ate ela expirar ou virar compra. */
    cupom: z.string().trim().min(1).max(40).optional(),
    /** Analise do checkout (spec 062): o codigo da visita e de onde ela veio, pra ligar a compra aos passos e a campanha. */
    visitante: Visitante.optional(),
    origem: OrigemDaVisita.optional(),
});
/**
 * `POST /v1/checkouts/:token/cotacao`: o preco e as parcelas da escolha (plataforma e beneficios), calculados no
 * servidor. A pagina chama quando a escolha muda; nunca soma nada sozinha.
 */
export const PedidoDeCotacao = z.object({
    plataforma: z.enum(["PROFIT_ONE", "PROFIT_PRO"]).optional(),
    beneficios: z.array(z.string().min(1)).max(8).default([]),
    /** Cupom que a pessoa digitou: a cotacao so confere e mostra o desconto; quem reserva o uso e a tentativa. */
    cupom: z.string().trim().min(1).max(40).optional(),
});
/** Pagamento no cartao (4.1, passo 4). O cartao foi tokenizado no navegador direto na Pagar.me. */
export const PagamentoNoCartao = z.object({
    card_token: z.string().trim().min(5).max(100),
    parcelas: z.number().int().min(1).max(12),
    endereco: z.object({
        cep: z.string().trim().min(8).max(9),
        linha: z.string().trim().min(3, "rua e numero").max(200),
        cidade: z.string().trim().min(2).max(100),
        uf: z.string().trim().length(2),
    }),
    /**
     * O total que a pagina mostrou (15/10, checkout editavel). Se o preco mudou enquanto a pessoa preenchia, o servico
     * recusa em vez de cobrar outro valor, e a pagina mostra o novo. Opcional: quem nao manda segue como antes.
     */
    total_centavos: z.number().int().min(1).optional(),
    /**
     * Os 4 ultimos digitos e a bandeira, que a Pagar.me devolve na tokenizacao (spec 070). O servico conta as recusas do
     * banco por cartao e pausa o que o banco recusou 3 vezes em 1 hora. Opcional: sem ele, conta pela tentativa.
     */
    cartao: z.object({ final: z.string().regex(/^\d{4}$/), bandeira: z.string().trim().min(1).max(30) }).optional(),
});
/**
 * Back redirect do checkout (spec 045, 3.10): so endereco `https://` completo, com dominio, ate 500 caracteres. Mora no
 * contrato pelo mesmo motivo do dolar: o painel recusa antes de mandar e a area confere antes de fazer link, com a MESMA
 * regra do servico de pagamentos (que confere de novo e e quem manda).
 */
export const ERRO_DO_VOLTAR_PARA = "Back redirect: um endereço https://... completo.";
const VOLTAR_PARA = /^https:\/\/[^\s/]+\.[^\s]+$/;
export function voltarParaValido(endereco) {
    const e = endereco.trim();
    return e.length <= 500 && VOLTAR_PARA.test(e);
}
// ---------------------------------------------------------------- cobranca avulsa (spec 003, 4.3)
/**
 * O backend pede uma cobranca (mensalidade ou portao do avanco). Idempotente pela `referencia`: pedir de novo devolve a
 * mesma. Rota interna, assinada com o segredo dos eventos.
 */
export const NovaCobranca = z.object({
    referencia: z.string().trim().min(3).max(120),
    pessoa: z.object({ nome: z.string().trim().min(1).max(120), cpf: z.string().trim().min(11).max(14), email: z.string().trim().email().max(200), telefone: z.string().trim().max(20).optional() }),
    descricao: z.string().trim().min(3).max(200),
    valor_centavos: z.number().int().positive().max(10_000_000),
    /** Dia do vencimento (AAAA-MM-DD), so pra mostrar. */
    vence_em: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});
//# sourceMappingURL=checkout.js.map